import { nip04, nip19 } from "nostr-tools"
import { finalizeEvent, getPublicKey } from "nostr-tools/pure"
import type { DraftEvent, NostrEvent } from "../types"
import { SignerError, type Signer } from "./types"

/**
 * In-memory signer for a locally entered `nsec`.
 *
 * The secret key is only ever held in a class field: it is never written to
 * localStorage, IndexedDB or any other persistent storage.
 */
export class LocalSigner implements Signer {
  readonly method = "nsec" as const
  private readonly secretKey: Uint8Array
  private readonly pubkey: string

  constructor(secretKey: Uint8Array) {
    if (!(secretKey instanceof Uint8Array) || secretKey.length !== 32) {
      throw new SignerError("invalid secret key", "invalid")
    }
    this.secretKey = secretKey
    this.pubkey = getPublicKey(secretKey)
  }

  /** Builds a signer from an `nsec1...` string. */
  static fromNsec(nsec: string): LocalSigner {
    const trimmed = nsec.trim()
    if (!trimmed.startsWith("nsec1")) throw new SignerError("expected an nsec1... key", "invalid")
    try {
      const decoded = nip19.decode(trimmed)
      if (decoded.type !== "nsec") throw new SignerError("expected an nsec1... key", "invalid")
      return new LocalSigner(decoded.data)
    } catch (error) {
      if (error instanceof SignerError) throw error
      throw new SignerError("could not decode the nsec key", "invalid")
    }
  }

  async getPublicKey(): Promise<string> {
    return this.pubkey
  }

  async signEvent(draft: DraftEvent): Promise<NostrEvent> {
    return finalizeEvent(
      {
        kind: draft.kind,
        content: draft.content,
        tags: draft.tags,
        created_at: draft.created_at,
      },
      this.secretKey,
    ) as NostrEvent
  }

  async nip04Encrypt(pubkey: string, plaintext: string): Promise<string> {
    return nip04.encrypt(this.secretKey, pubkey, plaintext)
  }

  async nip04Decrypt(pubkey: string, ciphertext: string): Promise<string> {
    return nip04.decrypt(this.secretKey, pubkey, ciphertext)
  }
}

/** Read-only session: browsing with a public key only. */
export class ReadOnlySigner implements Signer {
  readonly method = "npub" as const
  private readonly pubkey: string

  constructor(pubkey: string) {
    this.pubkey = pubkey
  }

  async getPublicKey(): Promise<string> {
    return this.pubkey
  }

  async signEvent(): Promise<NostrEvent> {
    throw new SignerError("this session is read-only; log in with a signer to publish", "read-only")
  }
}
